import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Select, MenuItem, FormControl,
  InputLabel, CircularProgress,
} from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const STAT_LABELS = {
  anyA:           { label: 'ANY/A', desc: 'Adjusted Net Yards per Attempt', higherBetter: true },
  snapPct:        { label: 'Snap %', desc: 'Snap Percentage', higherBetter: true },
  fantasyPprPg:   { label: 'FPTS/G', desc: 'Fantasy PPR Points per Game', higherBetter: true },
  passEpaPerAtt:  { label: 'EPA/Att', desc: 'Expected Points Added per Attempt', higherBetter: true },
  passAirYdsPct:  { label: 'Air Yds%', desc: 'Air Yards Percentage', higherBetter: true },
  dakota:         { label: 'DAKOTA', desc: 'DAKOTA composite QB metric', higherBetter: true },
};

const glassCard = {
  background: 'rgba(30,41,59,0.8)',
  border: '1px solid rgba(245,166,35,0.25)',
  borderRadius: 2,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
};

function StatRow({ label, desc, val1, val2 }) {
  const n1 = typeof val1 === 'number' ? val1 : parseFloat(val1);
  const n2 = typeof val2 === 'number' ? val2 : parseFloat(val2);
  const hasData = !isNaN(n1) && !isNaN(n2);
  const winner1 = hasData && n1 > n2;
  const winner2 = hasData && n2 > n1;

  const fmt = (v) => {
    if (v == null || v === '' || (typeof v === 'number' && isNaN(v))) return '—';
    if (typeof v === 'number') return v.toFixed(2);
    return v;
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      py: 1.5,
      px: 2,
      borderBottom: '1px solid rgba(245,166,35,0.12)',
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* QB1 value */}
      <Typography sx={{
        fontFamily: 'monospace',
        fontWeight: 800,
        fontSize: '1.1rem',
        color: winner1 ? '#10b981' : winner2 ? '#ef4444' : '#94a3b8',
        textAlign: 'right',
        pr: 2,
        transition: 'color 0.2s',
      }}>
        {fmt(val1)}
        {winner1 && <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', verticalAlign: 'middle' }}>▲</Box>}
      </Typography>

      {/* Center label */}
      <Box sx={{ textAlign: 'center', minWidth: 100 }}>
        <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.78rem' }}>
          {label}
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '0.65rem', letterSpacing: '-0.01em' }}>
          {desc}
        </Typography>
      </Box>

      {/* QB2 value */}
      <Typography sx={{
        fontFamily: 'monospace',
        fontWeight: 800,
        fontSize: '1.1rem',
        color: winner2 ? '#10b981' : winner1 ? '#ef4444' : '#94a3b8',
        textAlign: 'left',
        pl: 2,
        transition: 'color 0.2s',
      }}>
        {winner2 && <Box component="span" sx={{ mr: 0.5, fontSize: '0.7rem', verticalAlign: 'middle' }}>▲</Box>}
        {fmt(val2)}
      </Typography>
    </Box>
  );
}

export default function QBComparePage() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qb1Id, setQb1Id] = useState('');
  const [qb2Id, setQb2Id] = useState('');

  useEffect(() => {
    fetch('/data/player_season_2024.json')
      .then(r => r.json())
      .then(data => {
        const qbs = data
          .filter(p => p.pos === 'QB' && p.snapPct > 20)
          .sort((a, b) => {
            const nameA = (a.playerName || a.player_name || a.name || '');
            const nameB = (b.playerName || b.player_name || b.name || '');
            return nameA.localeCompare(nameB);
          });
        setPlayers(qbs);
        if (qbs.length >= 2) {
          setQb1Id(qbs[0].player_id || 0);
          setQb2Id(qbs[1].player_id || 1);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getPlayer = (id) => players.find((p, i) => (p.player_id || i) === id);
  const qb1 = getPlayer(qb1Id);
  const qb2 = getPlayer(qb2Id);

  const getName = (p) => p?.playerName || p?.player_name || p?.name || 'Unknown';
  const getTeam = (p) => p?.team || p?.recent_team || '—';

  const selectSx = {
    color: '#f1f5f9',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6,182,212,0.3)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f5a623' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f5a623' },
    '& .MuiSvgIcon-root': { color: '#94a3b8' },
    backgroundColor: 'rgba(30,41,59,0.6)',
    borderRadius: 1,
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'transparent' }}>
      <Navbar />

      <Container maxWidth="md" sx={{ py: 6, flex: 1 }}>
        <Typography variant="h4" sx={{
          fontWeight: 900,
          color: '#f1f5f9',
          mb: 0.5,
          background: 'linear-gradient(135deg, #10b981 0%, #f5a623 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          ⚖️ QB Comparison
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 4, fontSize: '0.9rem' }}>
          Compare any two QBs side-by-side across key efficiency metrics.
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#10b981' }} />
            <Typography sx={{ color: '#64748b', mt: 2 }}>Loading QB data…</Typography>
          </Box>
        ) : players.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: '#ef4444' }}>Failed to load player data.</Typography>
          </Box>
        ) : (
          <>
            {/* Dropdowns */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: 2,
              alignItems: 'center',
              mb: 4,
            }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#94a3b8' }}>QB 1</InputLabel>
                <Select
                  value={qb1Id}
                  label="QB 1"
                  onChange={e => setQb1Id(e.target.value)}
                  sx={selectSx}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(15,23,42,0.98)',
                        border: '1px solid rgba(245,166,35,0.30)',
                        '& .MuiMenuItem-root': { color: '#f1f5f9', fontSize: '0.85rem' },
                        '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(245,166,35,0.12)' },
                        '& .Mui-selected': { bgcolor: 'rgba(16,185,129,0.15) !important' },
                      },
                    },
                  }}
                >
                  {players.map((p, i) => (
                    <MenuItem key={p.player_id || i} value={p.player_id || i}>
                      {getName(p)} ({getTeam(p)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography sx={{ color: '#475569', fontWeight: 800, fontSize: '1.2rem', textAlign: 'center' }}>
                vs
              </Typography>

              <FormControl fullWidth>
                <InputLabel sx={{ color: '#94a3b8' }}>QB 2</InputLabel>
                <Select
                  value={qb2Id}
                  label="QB 2"
                  onChange={e => setQb2Id(e.target.value)}
                  sx={selectSx}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(15,23,42,0.98)',
                        border: '1px solid rgba(245,166,35,0.30)',
                        '& .MuiMenuItem-root': { color: '#f1f5f9', fontSize: '0.85rem' },
                        '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(245,166,35,0.12)' },
                        '& .Mui-selected': { bgcolor: 'rgba(16,185,129,0.15) !important' },
                      },
                    },
                  }}
                >
                  {players.map((p, i) => (
                    <MenuItem key={p.player_id || i} value={p.player_id || i}>
                      {getName(p)} ({getTeam(p)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Comparison Panel */}
            {qb1 && qb2 && (
              <Box sx={{ ...glassCard }}>
                {/* Header row */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  p: 2.5,
                  borderBottom: '1px solid rgba(245,166,35,0.25)',
                  background: 'rgba(245,166,35,0.08)',
                }}>
                  <Box sx={{ textAlign: 'right', pr: 2 }}>
                    <Typography sx={{ fontWeight: 900, color: '#10b981', fontSize: '1.1rem' }}>
                      {getName(qb1)}
                    </Typography>
                    <Chip label={getTeam(qb1)} />
                  </Box>
                  <Typography sx={{ color: '#475569', fontWeight: 700, textAlign: 'center', fontSize: '0.8rem' }}>
                    2024 Season
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography sx={{ fontWeight: 900, color: '#f5a623', fontSize: '1.1rem' }}>
                      {getName(qb2)}
                    </Typography>
                    <Chip label={getTeam(qb2)} right />
                  </Box>
                </Box>

                {/* Stat rows */}
                {Object.entries(STAT_LABELS).map(([key, meta]) => (
                  <StatRow
                    key={key}
                    label={meta.label}
                    desc={meta.desc}
                    val1={qb1[key]}
                    val2={qb2[key]}
                  />
                ))}
              </Box>
            )}

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 3, mt: 2.5, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>Higher value</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>Lower value</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#94a3b8' }} />
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>Equal / no data</Typography>
              </Box>
            </Box>
          </>
        )}
      </Container>

      <Footer />
    </Box>
  );
}

// Mini chip helper (no MUI Chip import needed for this simple use)
function Chip({ label, right }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block',
      bgcolor: 'rgba(245,166,35,0.20)',
      color: '#94a3b8',
      borderRadius: 1,
      px: 1,
      py: 0.2,
      fontSize: '0.68rem',
      fontWeight: 700,
      mt: 0.25,
    }}>
      {label}
    </Box>
  );
}
